import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const RollingBottleSection = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const bottleRotate = useTransform(scrollYProgress, [0, 1], [0, 720]);
  const bottleX = useTransform(scrollYProgress, [0, 1], [-200, 200]);

  const features = [
  {
    icon: "Thermometer",
    title: "24-Hour Temperature Control",
    description: "Advanced double-wall insulation keeps drinks hot for 12 hours and cold for 24 hours.",
    color: "text-red-500"
  },
  {
    icon: "Shield",
    title: "BPA-Free Materials",
    description: "Food-grade stainless steel construction ensures pure taste and complete safety.",
    color: "text-green-500"
  },
  {
    icon: "Droplets",
    title: "Leak-Proof Design",
    description: "Precision-engineered seals guarantee zero leaks in any position or activity.",
    color: "text-blue-500"
  },
  {
    icon: "Recycle",
    title: "100% Recyclable",
    description: "Sustainable materials and manufacturing process with minimal environmental impact.",
    color: "text-emerald-500"
  }];


  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16">

          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-accent/10 to-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Cog" size={16} className="text-accent animate-spin" />
            <span className="text-sm font-medium text-primary">Engineering Excellence</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-poppins font-bold text-text-primary mb-6">
            Precision Crafted for{' '}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Perfect Performance
            </span>
          </h2>
          
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Every Mava bottle is engineered with meticulous attention to detail, combining cutting-edge technology with sustainable materials.
          </p>
        </motion.div>

        {/* Rolling Bottle Animation */}
        <div className="relative mb-20">
          <div className="flex justify-center items-center h-64 relative overflow-hidden">
            {/* Track Line */}
            <div className="absolute inset-x-0 top-1/2 h-1 bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
            
            {/* Rolling Bottle */}
            <motion.div
              style={{
                x: bottleX,
                rotate: bottleRotate
              }}
              className="relative z-10">

              <Image
                src="https://images.unsplash.com/photo-1616170958305-bc56b25bc566"
                alt="Premium water bottle rolling across surface showing 360-degree view of sleek stainless steel design"
                className="w-32 h-48 object-contain drop-shadow-xl" />

            </motion.div>

            {/* Particle Effects */}
            {[...Array(8)]?.map((_, i) =>
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
                x: [0, i % 2 === 0 ? 100 : -100],
                y: [0, -50]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.3,
                ease: "easeOut"
              }}
              className={`absolute w-2 h-2 rounded-full ${
              i % 3 === 0 ? 'bg-primary' : i % 3 === 1 ? 'bg-accent' : 'bg-secondary'}`
              }
              style={{
                left: `${20 + i * 10}%`,
                top: '50%'
              }} />

            )}
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features?.map((feature, index) =>
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.6 }}
            viewport={{ once: true }}
            className="group">

              <div className="bg-white p-6 rounded-2xl liquid-shadow hover:shadow-xl brand-transition border border-slate-100 h-full">
                <div className="flex items-center space-x-4 mb-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center group-hover:scale-110 brand-transition`}>
                    <Icon name={feature?.icon} size={24} className={feature?.color} />
                  </div>
                  <h3 className="font-poppins font-semibold text-text-primary">
                    {feature?.title}
                  </h3>
                </div>
                
                <p className="text-text-secondary leading-relaxed">
                  {feature?.description}
                </p>

                {/* Progress Bar Animation */}
                <div className="mt-4 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: "100%" }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 1 }}
                  viewport={{ once: true }}
                  className={`h-full rounded-full ${
                  index % 4 === 0 ? 'bg-red-500' :
                  index % 4 === 1 ? 'bg-green-500' :
                  index % 4 === 2 ? 'bg-blue-500' : 'bg-emerald-500'}`
                  } />

                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Technical Specifications */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-16 bg-gradient-to-r from-primary/5 to-accent/5 rounded-3xl p-8 lg:p-12">

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-poppins font-bold text-text-primary mb-6">
                Technical Excellence
              </h3>
              
              <div className="grid grid-cols-2 gap-6">
                {[
                { label: "Capacity", value: "500ml - 1L" },
                { label: "Material", value: "316 Steel" },
                { label: "Insulation", value: "Vacuum Sealed" },
                { label: "Weight", value: "280g - 450g" }]?.
                map((spec, index) =>
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.4 }}
                  viewport={{ once: true }}
                  className="text-center p-4 bg-white/50 rounded-xl">

                    <div className="text-lg font-bold text-primary">{spec?.value}</div>
                    <div className="text-sm text-text-secondary">{spec?.label}</div>
                  </motion.div>
                )}
              </div>
            </div>

            <div className="relative">
              <motion.div
                animate={{
                  y: [0, -20, 0],
                  rotateY: [0, 10, 0]
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}>

                <Image
                  src="https://images.unsplash.com/photo-1628582357531-3da2e5a05875"
                  alt="Cross-section view of premium water bottle showing advanced insulation layers and precision engineering details"
                  className="w-full max-w-md mx-auto h-80 object-contain drop-shadow-2xl" />

              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>);

};

export default RollingBottleSection;